import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  Paper,
  Alert,
  CircularProgress,
  IconButton,
  Breadcrumbs,
  Link,
  Chip,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
  CheckCircle as CheckCircleIcon,
  Business as BusinessIcon,
  School as TrainingIcon,
  Handshake as MeetingIcon,
  Group as GroupIcon,
  BeachAccess as BeachIcon,
  Edit as EditIcon,
  LocationOn as LocationIcon,
  CalendarToday as CalendarIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { format, differenceInDays } from 'date-fns';
import api from '../config/api';
import {
  DOCUMENT,
  PAGE_CONTAINER_MAX_WIDTH,
  documentPageContainerSx,
  documentSelectSx,
  documentToggleBtnSx,
} from '../constants/documentTheme';
import { partnerOutlineBtnSx, partnerOxbloodBtnSx } from '../components/PartnerListChrome';
import LocationAutocomplete from '../components/LocationAutocomplete';
import DateRangePicker from '../components/DateRangePicker';
import { getTripLocationsArray } from '../utils/tripUtils';

const editTripFieldSx = {
  ...documentSelectSx,
  '& .MuiOutlinedInput-root': {
    borderRadius: 0,
    bgcolor: DOCUMENT.surface,
    fontFamily: DOCUMENT.sans,
    '& fieldset': { borderColor: DOCUMENT.ruleHard },
    '&:hover fieldset': { borderColor: DOCUMENT.accent },
    '&.Mui-focused fieldset': { borderColor: DOCUMENT.oxblood, borderWidth: 1 },
  },
};

const tripTypes = [
  { value: 'business', label: 'Business Travel', icon: <BusinessIcon /> },
  { value: 'conference', label: 'Conference', icon: <GroupIcon /> },
  { value: 'training', label: 'Training', icon: <TrainingIcon /> },
  { value: 'meeting', label: 'Meeting', icon: <MeetingIcon /> },
  { value: 'vacation', label: 'Vacation', icon: <BeachIcon /> },
];

const EditTrip = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [originalTrip, setOriginalTrip] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    locations: [{ name: '', coordinates: null }],
    startDateTime: null,
    endDateTime: null,
    tripType: 'business',
  });
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [hasChanges, setHasChanges] = useState(false);

  const selectedTripType = tripTypes.find(type => type.value === formData.tripType);

  // Location management functions
  const handleLocationChange = useCallback((index, newValue) => {
    setFormData(prev => ({
      ...prev,
      locations: prev.locations.map((loc, i) => 
        i === index ? { ...loc, name: newValue } : loc
      )
    }));
  }, []);

  const handleLocationSelect = useCallback((index, placeData) => {
    setFormData(prev => ({
      ...prev,
      locations: prev.locations.map((loc, i) => 
        i === index ? { 
          name: placeData.address, 
          coordinates: placeData.coordinates 
        } : loc
      )
    }));
  }, []);

  const addLocation = useCallback(() => {
    setFormData(prev => ({
      ...prev,
      locations: [...prev.locations, { name: '', coordinates: null }]
    }));
  }, []);

  const removeLocation = useCallback((index) => {
    if (formData.locations.length > 1) {
      setFormData(prev => ({
        ...prev,
        locations: prev.locations.filter((_, i) => i !== index)
      }));
    }
  }, [formData.locations.length]);

  const fetchTrip = useCallback(async () => {
    try {
      setError(null);
      const response = await api.get(`/trips/${id}`);
      const trip = response.data;
      
      // Handle both old single location and new multiple locations format
      let locations = [{ name: '', coordinates: null }];
      locations = getTripLocationsArray(trip);

      const tripData = {
        title: trip.title,
        description: trip.description || '',
        locations: locations,
        startDateTime: new Date(trip.start_date),
        endDateTime: new Date(trip.end_date),
        tripType: trip.trip_type,
      };
      
      setOriginalTrip(tripData);
      setFormData(tripData);
    } catch (err) {
      setError('Failed to load trip details');
      console.error('Error fetching trip:', err);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchTrip();
  }, [fetchTrip]);

  // Check for changes
  useEffect(() => {
    if (!originalTrip) return;
    
  const hasFormChanges = (
    formData.title !== originalTrip.title ||
    formData.description !== originalTrip.description ||
    JSON.stringify(formData.locations) !== JSON.stringify(originalTrip.locations) ||
    formData.tripType !== originalTrip.tripType ||
    formData.startDateTime?.getTime() !== originalTrip.startDateTime?.getTime() ||
    formData.endDateTime?.getTime() !== originalTrip.endDateTime?.getTime()
  );
    
    setHasChanges(hasFormChanges);
  }, [formData, originalTrip]);

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (validationErrors[name]) {
      setValidationErrors(prev => ({ ...prev, [name]: '' }));
    }
  }, [validationErrors]);

  const handleStartDateTimeChange = useCallback((dateTime) => {
    // Auto-set end time to one hour after start time
    const endDateTime = dateTime ? new Date(dateTime) : null;
    if (endDateTime) {
      endDateTime.setHours(endDateTime.getHours() + 1);
    }
    
    setFormData(prev => ({ 
      ...prev, 
      startDateTime: dateTime,
      endDateTime: endDateTime || prev.endDateTime // Keep existing end time if no start time
    }));
    if (validationErrors.startDateTime) {
      setValidationErrors(prev => ({ ...prev, startDateTime: '' }));
    }
  }, [validationErrors]);

  const handleEndDateTimeChange = useCallback((dateTime) => {
    setFormData(prev => ({ ...prev, endDateTime: dateTime }));
    if (validationErrors.endDateTime) {
      setValidationErrors(prev => ({ ...prev, endDateTime: '' }));
    }
  }, [validationErrors]);

  const validateForm = useCallback(() => {
    const errors = {};
    
    if (!formData.title.trim()) errors.title = 'Trip title is required';
    if (formData.title.length > 100) errors.title = 'Title must be less than 100 characters';
    
    // Validate locations
    const validLocations = formData.locations.filter(loc => loc.name && loc.name.trim());
    if (validLocations.length === 0) {
      errors.locations = 'At least one destination is required';
    }
    
    if (!formData.startDateTime) errors.startDateTime = 'Start date is required';
    if (!formData.endDateTime) errors.endDateTime = 'End date is required';
    if (formData.startDateTime && formData.endDateTime && formData.startDateTime >= formData.endDateTime) {
      errors.endDateTime = 'End date must be after start date';
    }
    if (!formData.tripType) errors.tripType = 'Please select a trip type';
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  }, [formData]);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setSaving(true);
    setError(null);
    setSuccess('');

    try {
      // Filter out empty locations
      const validLocations = formData.locations.filter(loc => loc.name && loc.name.trim());
      
      const tripData = {
        title: formData.title,
        description: formData.description,
        locations: validLocations,
        start_date: formData.startDateTime?.toISOString().split('T')[0],
        end_date: formData.endDateTime?.toISOString().split('T')[0],
        trip_type: formData.tripType
      };

      const response = await api.put(`/trips/${id}`, tripData);

      if (response.data) {
        setSuccess('Trip updated successfully! Redirecting...');
        setTimeout(() => {
          navigate(`/trips/${id}`);
        }, 2000);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update trip');
      console.error('Error updating trip:', err);
    } finally {
      setSaving(false);
    }
  }, [id, formData, navigate, validateForm]);

  const getTripDuration = () => {
    if (formData.startDateTime && formData.endDateTime) {
      const days = differenceInDays(formData.endDateTime, formData.startDateTime);
      return days > 0 ? `${days} day${days > 1 ? 's' : ''}` : '';
    }
    return '';
  };

  const isFormValid = () => {
    const validLocations = formData.locations.filter(loc => loc.name && loc.name.trim());
    return formData.title && validLocations.length > 0 && formData.startDateTime && 
           formData.endDateTime && formData.tripType;
  };

  if (loading) {
    return (
      <Container maxWidth={PAGE_CONTAINER_MAX_WIDTH} sx={{ ...documentPageContainerSx, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '70vh' }}>
        <CircularProgress sx={{ color: DOCUMENT.oxblood }} size={48} />
      </Container>
    );
  }

  if (!originalTrip) {
    return (
      <Container maxWidth={PAGE_CONTAINER_MAX_WIDTH} sx={{ ...documentPageContainerSx, py: 4 }}>
        <Alert
          severity="error"
          sx={{ borderRadius: 0, border: `1px solid ${DOCUMENT.rule}`, bgcolor: DOCUMENT.surface }}
        >
          Trip not found or you don't have permission to edit it.
        </Alert>
      </Container>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: DOCUMENT.canvas }}>
      <Box
        sx={{
          bgcolor: DOCUMENT.nav,
          color: DOCUMENT.ivory,
          pt: { xs: 3, md: 4 },
          pb: { xs: 3, md: 4 },
          px: { xs: 2, md: 0 },
          borderBottom: `1px solid ${DOCUMENT.navRule}`,
        }}
      >
        <Container maxWidth={PAGE_CONTAINER_MAX_WIDTH} sx={documentPageContainerSx}>
          <Breadcrumbs
            separator="›"
            sx={{
              mb: 2.5,
              '& .MuiBreadcrumbs-separator': { color: DOCUMENT.navInk },
              '& .MuiBreadcrumbs-li': { color: DOCUMENT.navInk, fontFamily: DOCUMENT.sans },
            }}
          >
            <Link
              component="button"
              onClick={() => navigate('/trips')}
              sx={{
                color: DOCUMENT.navInk,
                textDecoration: 'none',
                fontFamily: DOCUMENT.sans,
                '&:hover': { color: DOCUMENT.ivory, textDecoration: 'underline' },
              }}
            >
              My Trips
            </Link>
            <Link
              component="button"
              onClick={() => navigate(`/trips/${id}`)}
              sx={{
                color: DOCUMENT.navInk,
                textDecoration: 'none',
                fontFamily: DOCUMENT.sans,
                '&:hover': { color: DOCUMENT.ivory, textDecoration: 'underline' },
              }}
            >
              {originalTrip?.title || 'Trip Details'}
            </Link>
            <Typography sx={{ color: DOCUMENT.ivory, fontFamily: DOCUMENT.sans, fontWeight: 500 }}>
              Edit Trip
            </Typography>
          </Breadcrumbs>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconButton
              onClick={() => navigate(`/trips/${id}`)}
              sx={{
                color: DOCUMENT.ivory,
                borderRadius: 0,
                border: `1px solid ${DOCUMENT.navRule}`,
                '&:hover': { bgcolor: 'rgba(246,239,228,0.08)' },
              }}
            >
              <ArrowBackIcon />
            </IconButton>

            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
                <EditIcon sx={{ color: DOCUMENT.navAccent, fontSize: 22 }} />
                <Typography
                  component="h1"
                  sx={{
                    fontFamily: DOCUMENT.display,
                    fontWeight: 400,
                    fontSize: { xs: '1.6rem', md: '2rem' },
                    lineHeight: 1.2,
                    color: DOCUMENT.ivory,
                  }}
                >
                  Edit Trip
                </Typography>
              </Box>
              <Typography sx={{ fontFamily: DOCUMENT.sans, fontSize: 14, color: DOCUMENT.navInk }}>
                Update your trip details and preferences
              </Typography>
              {hasChanges && (
                <Chip
                  icon={<EditIcon />}
                  label="Unsaved Changes"
                  size="small"
                  sx={{
                    mt: 1.5,
                    borderRadius: 0,
                    bgcolor: 'transparent',
                    color: DOCUMENT.navAccent,
                    border: `1px solid ${DOCUMENT.navAccent}`,
                    fontFamily: DOCUMENT.sans,
                    fontSize: 11,
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase',
                    '& .MuiChip-icon': { color: DOCUMENT.navAccent },
                  }}
                />
              )}
            </Box>
          </Box>
        </Container>
      </Box>

      <Container
        maxWidth={PAGE_CONTAINER_MAX_WIDTH}
        sx={{ ...documentPageContainerSx, py: { xs: 3, md: 4 } }}
      >
        {error && (
          <Alert severity="error" sx={{ mb: 3, borderRadius: 0, border: `1px solid ${DOCUMENT.rule}` }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 3, borderRadius: 0, border: `1px solid ${DOCUMENT.rule}` }}>
            {success}
          </Alert>
        )}

        <Paper
          elevation={0}
          sx={{
            borderRadius: 0,
            border: `1px solid ${DOCUMENT.rule}`,
            bgcolor: DOCUMENT.surface,
            position: 'relative',
          }}
        >
          <form onSubmit={handleSubmit}>
            <Box sx={{ p: 4 }}>
              <Grid container spacing={4}>
                {/* Left Column - Form Fields */}
                <Grid item xs={12} md={8}>
                  <Grid container spacing={3}>
                    {/* Trip Title */}
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Trip Title"
                        name="title"
                        value={formData.title}
                        onChange={handleChange}
                        disabled={saving}
                        required
                        error={!!validationErrors.title}
                        helperText={validationErrors.title}
                        placeholder="Enter a descriptive title for your trip"
                        sx={editTripFieldSx}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Description (Optional)"
                        name="description"
                        value={formData.description}
                        onChange={handleChange}
                        disabled={saving}
                        multiline
                        rows={3}
                        placeholder="Add any additional details about your trip..."
                        sx={editTripFieldSx}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <Typography
                        sx={{
                          mb: 2,
                          fontFamily: DOCUMENT.sans,
                          fontSize: 12,
                          letterSpacing: '0.12em',
                          textTransform: 'uppercase',
                          color: DOCUMENT.ink3,
                        }}
                      >
                        Destinations
                      </Typography>
                      {formData.locations.map((location, index) => (
                        <Box key={index} sx={{ mb: 2, display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                          <Box sx={{ flex: 1 }}>
                            <LocationAutocomplete
                              value={location.name}
                              onChange={(newValue) => handleLocationChange(index, newValue)}
                              onPlaceSelect={(placeData) => handleLocationSelect(index, placeData)}
                              label={`Destination ${index + 1}`}
                              required
                              fullWidth
                              disabled={saving}
                              error={!!validationErrors.locations}
                              helperText={validationErrors.locations}
                              placeholder="Where are you going?"
                              sx={editTripFieldSx}
                            />
                          </Box>
                          {formData.locations.length > 1 && (
                            <Button
                              onClick={() => removeLocation(index)}
                              disabled={saving}
                              sx={{ ...partnerOutlineBtnSx, minWidth: 'auto', px: 2 }}
                            >
                              Remove
                            </Button>
                          )}
                        </Box>
                      ))}
                      <Button
                        startIcon={<AddIcon />}
                        onClick={addLocation}
                        disabled={saving}
                        sx={{ ...partnerOutlineBtnSx, mt: 1, borderStyle: 'dashed' }}
                      >
                        Add Destination
                      </Button>
                    </Grid>

                    {/* Dates */}
                    <Grid item xs={12}>
                      <DateRangePicker
                        label="Trip Dates & Times"
                        startDateTime={formData.startDateTime}
                        endDateTime={formData.endDateTime}
                        onStartDateTimeChange={handleStartDateTimeChange}
                        onEndDateTimeChange={handleEndDateTimeChange}
                        required
                        showTimeSelection={true}
                        error={!!validationErrors.startDateTime || !!validationErrors.endDateTime}
                        helperText={validationErrors.startDateTime || validationErrors.endDateTime}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <Typography
                        sx={{
                          mb: 2,
                          fontFamily: DOCUMENT.sans,
                          fontSize: 12,
                          letterSpacing: '0.12em',
                          textTransform: 'uppercase',
                          color: DOCUMENT.ink3,
                        }}
                      >
                        Trip Type
                      </Typography>
                      <Grid container spacing={1.5}>
                        {tripTypes.map((type) => {
                          const selected = formData.tripType === type.value;
                          return (
                            <Grid item xs={6} sm={4} key={type.value}>
                              <Button
                                fullWidth
                                disabled={saving}
                                onClick={() => setFormData((prev) => ({ ...prev, tripType: type.value }))}
                                startIcon={type.icon}
                                sx={{
                                  ...documentToggleBtnSx(selected),
                                  py: 1.5,
                                  justifyContent: 'flex-start',
                                }}
                              >
                                {type.label}
                                {selected ? (
                                  <CheckCircleIcon sx={{ ml: 'auto', fontSize: 16, color: DOCUMENT.ivory }} />
                                ) : null}
                              </Button>
                            </Grid>
                          );
                        })}
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>

                <Grid item xs={12} md={4}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 3,
                      bgcolor: DOCUMENT.canvas,
                      borderRadius: 0,
                      border: `1px solid ${DOCUMENT.rule}`,
                      height: 'fit-content',
                      position: 'sticky',
                      top: 24,
                    }}
                  >
                    <Typography
                      sx={{
                        mb: 2,
                        fontFamily: DOCUMENT.display,
                        fontWeight: 400,
                        fontSize: '1.25rem',
                        color: DOCUMENT.ink,
                      }}
                    >
                      Updated Trip Preview
                    </Typography>

                    {formData.title && (
                      <Box sx={{ mb: 2 }}>
                        <Typography sx={{ fontSize: 11, letterSpacing: '0.1em', textTransform: 'uppercase', color: DOCUMENT.ink3 }}>
                          Title
                        </Typography>
                        <Typography sx={{ fontWeight: 500, color: DOCUMENT.ink, fontFamily: DOCUMENT.sans }}>
                          {formData.title}
                        </Typography>
                      </Box>
                    )}

                    {formData.locations.some((loc) => loc.name && loc.name.trim()) && (
                      <Box sx={{ mb: 2 }}>
                        <Typography sx={{ fontSize: 11, letterSpacing: '0.1em', textTransform: 'uppercase', color: DOCUMENT.ink3 }}>
                          Destinations
                        </Typography>
                        {formData.locations
                          .filter((loc) => loc.name && loc.name.trim())
                          .map((location, index) => (
                            <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                              <LocationIcon sx={{ fontSize: 16, color: DOCUMENT.ink3 }} />
                              <Typography sx={{ fontWeight: 500, color: DOCUMENT.ink, fontFamily: DOCUMENT.sans }}>
                                {location.name}
                              </Typography>
                            </Box>
                          ))}
                      </Box>
                    )}

                    {formData.startDateTime && formData.endDateTime && (
                      <Box sx={{ mb: 2 }}>
                        <Typography sx={{ fontSize: 11, letterSpacing: '0.1em', textTransform: 'uppercase', color: DOCUMENT.ink3 }}>
                          Dates
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <CalendarIcon sx={{ fontSize: 16, color: DOCUMENT.ink3 }} />
                          <Typography sx={{ fontWeight: 500, color: DOCUMENT.ink, fontFamily: DOCUMENT.sans }}>
                            {format(formData.startDateTime, 'MMM d')} - {format(formData.endDateTime, 'MMM d, yyyy')}
                          </Typography>
                        </Box>
                        {getTripDuration() && (
                          <Typography sx={{ ml: 3, fontSize: 13, color: DOCUMENT.ink3 }}>
                            {getTripDuration()}
                          </Typography>
                        )}
                      </Box>
                    )}

                    {selectedTripType && (
                      <Box sx={{ mb: 2 }}>
                        <Typography sx={{ fontSize: 11, letterSpacing: '0.1em', textTransform: 'uppercase', color: DOCUMENT.ink3 }}>
                          Type
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box sx={{ color: DOCUMENT.oxblood, display: 'flex' }}>
                            {selectedTripType.icon}
                          </Box>
                          <Typography sx={{ fontWeight: 500, color: DOCUMENT.ink, fontFamily: DOCUMENT.sans }}>
                            {selectedTripType.label}
                          </Typography>
                        </Box>
                      </Box>
                    )}

                    {formData.description && (
                      <Box sx={{ mb: 2 }}>
                        <Typography sx={{ fontSize: 11, letterSpacing: '0.1em', textTransform: 'uppercase', color: DOCUMENT.ink3 }}>
                          Description
                        </Typography>
                        <Typography sx={{ fontSize: 14, fontStyle: 'italic', color: DOCUMENT.ink2, fontFamily: DOCUMENT.sans }}>
                          {formData.description}
                        </Typography>
                      </Box>
                    )}

                    {hasChanges && (
                      <Alert
                        severity="info"
                        sx={{
                          mt: 3,
                          borderRadius: 0,
                          border: `1px solid ${DOCUMENT.rule}`,
                          bgcolor: DOCUMENT.ivory,
                          fontSize: '0.875rem',
                        }}
                      >
                        You have unsaved changes
                      </Alert>
                    )}
                  </Paper>
                </Grid>
              </Grid>

              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  pt: 4,
                  mt: 4,
                  borderTop: `1px solid ${DOCUMENT.rule}`,
                  gap: 2,
                  flexWrap: 'wrap',
                }}
              >
                <Button
                  onClick={() => navigate(`/trips/${id}`)}
                  disabled={saving}
                  sx={partnerOutlineBtnSx}
                >
                  Cancel
                </Button>

                <Button
                  type="submit"
                  disabled={!isFormValid() || saving || !hasChanges}
                  sx={partnerOxbloodBtnSx}
                >
                  {saving ? (
                    <>
                      <CircularProgress size={16} sx={{ mr: 1, color: DOCUMENT.ivory }} />
                      Saving Changes...
                    </>
                  ) : (
                    <>
                      <SaveIcon sx={{ mr: 1, fontSize: 16 }} />
                      Save Changes
                    </>
                  )}
                </Button>
              </Box>
            </Box>
          </form>
        </Paper>
      </Container>
    </Box>
  );
};

export default EditTrip; 